/**
 * FantasyCalc — market values (§4.3.3).
 *
 * **Browser-direct.** `api.fantasycalc.com` serves `access-control-allow-origin: *`
 * (re-verified 2026-07-27), so no proxy is needed and no user data touches this
 * project's infrastructure.
 *
 * Two caveats are encoded here and surfaced in the UI rather than left in a
 * comment:
 *
 *  - **Tier 3.** FantasyCalc publishes no documented public developer API, so
 *    this is courtesy use: one cached call per session, never a poll.
 *  - **It is not a consensus ranking.** These values come from real trade
 *    activity, not expert opinion, so the source is labelled "market value" in
 *    the UI. It answers a different question than a ranking list and will
 *    disagree in predictable directions — it overrates known commodities and
 *    lags on rookies and news. Presenting it as "consensus" would quietly
 *    mislead.
 *
 * The payload carries a `sleeperId` per player, which makes name matching
 * essentially free for this source (§4.3.3) — no fuzzy matching, no unmatched
 * report, no aliases needed.
 */

import type { PlayerUniverse, Position, RankingSource, RankingRow } from '../core/types.ts';
import { fetchWithRetry, parseJsonByContent, RateLimiter } from './http.ts';

const BASE = 'https://api.fantasycalc.com/values/current';
const limiter = new RateLimiter(1);

export const FANTASYCALC_SOURCE_ID = 'fantasycalc';

interface FantasyCalcEntry {
  player: {
    id: number;
    name: string;
    sleeperId?: string | null;
    position?: string | null;
    maybeTeam?: string | null;
  };
  value: number;
  overallRank: number;
  positionRank: number;
  trend30Day?: number;
  maybeTier?: number | null;
}

export interface FantasyCalcOptions {
  teams: number;
  /** Reception points, passed straight through as the `ppr` parameter. */
  ppr: number;
  /** 2 for superflex/2QB leagues, 1 otherwise. */
  numQbs: number;
  signal?: AbortSignal;
}

/**
 * Load FantasyCalc market values as a ranking source.
 *
 * League shape is passed through to the API rather than defaulted, so a
 * 10-team superflex league gets superflex values — QB pricing is the single
 * biggest way market value differs between formats, and using 1QB values in a
 * superflex draft would be actively misleading.
 */
export async function loadFantasyCalc(opts: FantasyCalcOptions): Promise<RankingSource> {
  const url = new URL(BASE);
  url.searchParams.set('isDynasty', 'false');
  url.searchParams.set('numQbs', String(opts.numQbs));
  url.searchParams.set('numTeams', String(opts.teams));
  url.searchParams.set('ppr', String(opts.ppr));

  await limiter.acquire();
  const res = await fetchWithRetry(url.toString(), { signal: opts.signal, timeoutMs: 15_000 });
  const raw = await parseJsonByContent<FantasyCalcEntry[]>(res);

  const rows: RankingRow[] = [];
  for (const entry of raw) {
    const sleeperId = entry.player.sleeperId?.trim();
    // No Sleeper id means we cannot place this player on the board with
    // certainty. Rather than fuzzy-matching a name we do not need, skip it —
    // FantasyCalc includes plenty of deep-dynasty names Sleeper has never
    // heard of, and forcing a match would create false positives.
    if (!sleeperId) continue;

    rows.push({
      source_id: FANTASYCALC_SOURCE_ID,
      player_key: sleeperId,
      raw_name: entry.player.name,
      overall_rank: entry.overallRank,
      position_rank: entry.positionRank ?? null,
      tier: entry.maybeTier ?? null,
      team: entry.player.maybeTeam ?? null,
      position: asPosition(entry.player.position),
      notes:
        entry.trend30Day !== undefined && entry.trend30Day !== 0
          ? `30-day trend ${entry.trend30Day > 0 ? '+' : ''}${entry.trend30Day}`
          : null,
    });
  }

  rows.sort((a, b) => a.overall_rank - b.overall_rank);

  return {
    source_id: FANTASYCALC_SOURCE_ID,
    source_name: 'FantasyCalc market value',
    source_url: 'https://fantasycalc.com/',
    as_of_date: new Date().toISOString().slice(0, 10),
    scoring_format: opts.ppr >= 0.9 ? 'ppr' : opts.ppr >= 0.4 ? 'half-ppr' : 'standard',
    tier: 3,
    kind: 'market',
    rows,
    unmatched: [],
  };
}

/**
 * Drop rows for players the current Sleeper universe does not contain.
 *
 * Called after load so a stale player cache cannot put a phantom row on the
 * board. Kept separate from `loadFantasyCalc` so the loader stays testable
 * without a universe.
 */
export function reconcileWithUniverse(
  source: RankingSource,
  universe: PlayerUniverse,
): RankingSource {
  const rows = source.rows.filter((r) => r.player_key !== null && universe.has(r.player_key));
  return {
    ...source,
    rows: rows.map((r, i) => ({ ...r, overall_rank: i + 1 })),
  };
}

function asPosition(value: string | null | undefined): Position | null {
  if (!value) return null;
  const v = value.toUpperCase();
  if (v === 'DST') return 'DEF';
  return (['QB', 'RB', 'WR', 'TE', 'K', 'DEF'] as const).includes(v as never)
    ? (v as Position)
    : null;
}
